import { Component, Input, EventEmitter, Output, SimpleChanges, OnChanges, OnInit, inject } from '@angular/core';
import { QModelComponent, ApiService } from 'projects/visualize3d/src/public-api';
import { Validators, FormControl, FormArray, FormGroup, FormBuilder } from '@angular/forms';
import { finalize, take } from 'rxjs/operators';
import { validateQModelParameter } from '../../validators/q-model-parameter-validator';
import { QModelViewMode } from '../../../../../visualize3d/src/lib/models/q-model-view-mode';

type KeyValueForm = {
  key: FormControl<any>;
  value: FormControl<any>;
};

type QModelComponentForm = {
  id: FormControl<string>;
  detailMode: FormControl<number>;
  lodLevel: FormControl<number>;
  platform: FormControl<number>;
  viewMode: FormControl<QModelViewMode>;
  dimensionLineMode: FormControl<number>;
  calculationResolution: FormControl<number>;
  parameters: FormArray<FormGroup<KeyValueForm>>;
  tags: FormArray<FormGroup<KeyValueForm>>;
};

@Component({
  selector: 'app-q-model-component-editor',
  templateUrl: './q-model-component-editor.component.html',
  styleUrls: ['./q-model-component-editor.component.css'],
  standalone: false,
})
export class QModelComponentEditorComponent implements OnChanges, OnInit {
  private fb = inject(FormBuilder);
  private api = inject(ApiService);

  @Input() component: QModelComponent;
  @Output() componentChange = new EventEmitter();

  qModelForm: FormGroup<QModelComponentForm>;
  qModelIds: string[];
  isLoading = false;

  constructor() {
    this.qModelForm = this.fb.group({
      id: [''],
      detailMode: [0],
      lodLevel: [0],
      platform: [0],
      viewMode: [0],
      dimensionLineMode: [0],
      calculationResolution: [0],
      parameters: this.fb.array([
        this.fb.group({
          key: [''],
          value: [''],
        }),
      ]),
      tags: this.fb.array([
        this.fb.group({
          key: [''],
          value: [''],
        }),
      ]),
    });

    // Subscribe to valuechanges to apply relevant business rules
    this.qModelForm.valueChanges.subscribe(formData => {
      // Get "parameters" -formArray from qmodel form
      const params = this.qModelForm.controls.parameters as QModelComponentForm['parameters'];
      // Find a group from the parameters array where the value of the "key" -property is "CAT"
      const catControlIndex = params.controls.findIndex(control => control.value?.key === 'CAT');
      const catValue = params.controls[catControlIndex];
      // Find a group from the parameters array where the value of the "key" -property is "CAT"
      const bbvisValueIndex = params.controls.findIndex(control => control.value?.key === 'BBVIS');
      const bbvisValue = params.controls[bbvisValueIndex];

      // if the detail is CAT
      if (formData.detailMode === 1) {
        // add CAT is not defined
        if (!catValue) {
          // Use formBuilder to create a new form group with two fields: key(string type) and value(number type)
          const catGroup = this.fb.group({ key: ['CAT'], value: [1] });

          // Add to params array
          params.push(catGroup);
        }
      } else {
        // remove CAT if not CAT mode
        if (catValue) {
          params.removeAt(catControlIndex);
        }
      }

      // if the detail is lod and LOD 200 selected
      if (formData.detailMode === 2 && formData.lodLevel === 1) {
        // add CAT is not defined
        if (!bbvisValue) {
          // Use formBuilder to create a new form group with two fields: key(string type) and value(number type)
          const bbvisGroup = this.fb.group({ key: ['BBVIS'], value: [1] });

          // Add to params array
          params.push(bbvisGroup);
        }
      } else {
        // remove BBVIS if not LOD 200 mode
        if (bbvisValue) {
          params.removeAt(bbvisValueIndex);
        }
      }

      // Tell form to update & submit
      this.qModelForm.updateValueAndValidity({ emitEvent: false });
      this.onSubmit();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.component) {
      this.setFormValuesFrom(changes.component.currentValue);
    }
  }

  ngOnInit(): void {
    this.isLoading = true;
    this.api
      .getQModelIds()
      .pipe(
        finalize(() => (this.isLoading = false)),
        take(1),
      )
      .subscribe(ids => {
        this.qModelIds = ids;
      });

    this.qModelForm.controls.id.valueChanges.subscribe(() => {
      this.onSubmit();
    });
  }

  onSubmit(): void {
    this.component.id = this.qModelForm.controls.id.value;
    this.component.parameters = this.parameters.controls
      .filter(p => !!p.controls.key.value || !!p.controls.value.value)
      .map(p => {
        return { key: p.controls.key.value, value: p.controls.value.value };
      });
    this.component.tags = this.tags.controls
      .filter(p => !!p.controls.key.value || !!p.controls.value.value)
      .map(p => {
        return { key: p.controls.key.value, value: p.controls.value.value };
      });

    this.component.detailMode = this.qModelForm.controls.detailMode.value;
    this.component.lodLevel = this.qModelForm.controls.lodLevel.value;
    this.component.platform = this.qModelForm.controls.platform.value;
    this.component.viewMode = this.qModelForm.controls.viewMode.value;
    this.component.dimensionLineMode = this.qModelForm.controls.dimensionLineMode.value;
    this.component.calculationResolution = this.qModelForm.controls.calculationResolution.value;

    this.componentChange.emit();
  }

  get parameters(): QModelComponentForm['parameters'] {
    return this.qModelForm.get('parameters') as QModelComponentForm['parameters'];
  }

  get tags(): QModelComponentForm['tags'] {
    return this.qModelForm.get('tags') as QModelComponentForm['tags'];
  }

  parameterBlur(): void {
    this.qModelForm.updateValueAndValidity();
    if (!this.qModelForm.valid) {
      return;
    }
    this.onSubmit();
  }

  tagBlur(): void {
    this.qModelForm.updateValueAndValidity();
    if (!this.qModelForm.valid) {
      return;
    }
    this.onSubmit();
  }

  removeParameter(index: number): void {
    this.parameters.removeAt(index);
    this.onSubmit();
  }

  removeTag(index: number): void {
    this.tags.removeAt(index);
    this.onSubmit();
  }

  private setFormValuesFrom(qModel: QModelComponent): void {
    this.qModelForm.controls.id.setValue(qModel.id, { emitEvent: false });

    const parameterGroups = qModel.parameters.map(p =>
      this.fb.group(
        {
          key: [p.key, Validators.required],
          value: [p.value as any, Validators.required],
        },
        { updateOn: 'change' },
      ),
    );

    parameterGroups.push(
      this.fb.group(
        {
          key: [''],
          value: [''],
        },
        { updateOn: 'change', validators: validateQModelParameter },
      ),
    );

    this.qModelForm.controls.parameters = this.fb.array(parameterGroups);

    const tagGroups = qModel.tags.map(p =>
      this.fb.group(
        {
          key: [p.key, Validators.required],
          value: [p.value, Validators.required],
        },
        { updateOn: 'change' },
      ),
    );
    tagGroups.push(
      this.fb.group(
        {
          key: [''],
          value: [''],
        },
        { updateOn: 'change', validators: validateQModelParameter },
      ),
    );

    this.qModelForm.controls.tags = this.fb.array(tagGroups);

    this.qModelForm.controls.detailMode.setValue(qModel.detailMode, { emitEvent: false });
    this.qModelForm.controls.lodLevel.setValue(qModel.lodLevel, { emitEvent: false });
    this.qModelForm.controls.platform.setValue(qModel.platform, { emitEvent: false });
    this.qModelForm.controls.viewMode.setValue(qModel.viewMode, { emitEvent: false });
    this.qModelForm.controls.dimensionLineMode.setValue(qModel.dimensionLineMode, { emitEvent: false });
    this.qModelForm.controls.calculationResolution.setValue(qModel.calculationResolution, { emitEvent: false });
  }
}
